import React from 'react';
import { Phone } from './types';
import { validatePhoneNumber, formatPhoneNumber } from '../../../utils/validation';
import { CategoryDropdown } from '../category';
import '../../../components/common/CRMForm.css';

interface PhoneSectionProps {
  phones: Phone[];
  onChange: (phones: Phone[]) => void;
}

const PhoneSection: React.FC<PhoneSectionProps> = ({ phones, onChange }) => {
  const addPhone = () => {
    const newPhone: Phone = {
      id: 0, // 0 indicates a new phone
      contactId: 0, // Will be set when saving
      typeId: 0, // No selection - user must choose
      phoneNumber: '',
      isPrimary: phones.length === 0 // First phone is primary by default
    };
    onChange([...phones, newPhone]);
  };

  const updatePhone = (index: number, field: keyof Phone, value: any) => {
    const updatedPhones = phones.map((phone, i) => {
      if (i === index) {
        const updatedPhone = { ...phone, [field]: value };
        
        // Format phone number when it changes
        if (field === 'phoneNumber') {
          updatedPhone.phoneNumber = formatPhoneNumber(value);
        }
        
        // If setting this phone as primary, unset others
        if (field === 'isPrimary' && value) {
          // First unset all others
          const resetPhones = phones.map(p => ({ ...p, isPrimary: false }));
          resetPhones[index] = updatedPhone;
          return updatedPhone;
        }
        
        return updatedPhone;
      }
      
      // Unset primary for other phones if current phone is being set as primary
      if (field === 'isPrimary' && value) {
        return { ...phone, isPrimary: false };
      }
      
      return phone;
    });
    
    onChange(updatedPhones);
  };

  const removePhone = (index: number) => {
    const updatedPhones = phones.filter((_, i) => i !== index);
    
    // If we removed the primary phone and there are still phones left,
    // make the first one primary
    const removedPhone = phones[index];
    if (removedPhone.isPrimary && updatedPhones.length > 0) {
      updatedPhones[0].isPrimary = true;
    }
    
    onChange(updatedPhones);
  };

  const isValidPhone = (phoneNumber: string) => {
    return phoneNumber.trim() === '' || validatePhoneNumber(phoneNumber);
  };

  return (
    <div className="form-section-content">
      {phones.map((phone, index) => (
        <div key={phone.id} className="form-item-group">
          <div className="app-form-row">
            <div className="app-form-group">
              <label className="app-form-label">Phone Type</label>
              <CategoryDropdown
                parentType="phone"
                value={phone.typeId}
                onChange={(value) => updatePhone(index, 'typeId', value)}
                required={true}
                className={phone.typeId === 0 ? 'error' : ''}
              />
              {phone.typeId === 0 && (
                <div className="app-form-error">Please select a phone type</div>
              )}
            </div>

            <div className="app-form-group">
              <label className="app-form-label">Phone Number</label>
              <input
                type="tel"
                className={`app-form-input ${!isValidPhone(phone.phoneNumber) ? 'error' : ''}`}
                value={phone.phoneNumber}
                onChange={(e) => updatePhone(index, 'phoneNumber', e.target.value)}
                placeholder="(123) 456-7890"
              />
              {!isValidPhone(phone.phoneNumber) && (
                <div className="app-form-error">Please enter a valid phone number</div>
              )}
            </div>
          </div>

          <div className="app-form-group">
            <label className="app-checkbox-label">
              <input
                type="checkbox"
                className="app-form-checkbox"
                checked={phone.isPrimary}
                onChange={(e) => updatePhone(index, 'isPrimary', e.target.checked)}
              />
              Primary phone number
            </label>
          </div>

          <div className="form-item-actions">
            <button
              type="button"
              className="app-btn-delete"
              onClick={() => removePhone(index)}
              title="Delete phone"
            >
            </button>
          </div>
        </div>
      ))}

      <button
        type="button"
        className="app-btn app-btn-secondary"
        onClick={addPhone}
      >
        Add Phone Number
      </button>

      {phones.length > 0 && !phones.some(p => p.isPrimary) && (
        <div className="form-warning">
          No primary phone number selected. Please mark one as primary.
        </div>
      )}
    </div>
  );
};

export default PhoneSection;
